import { randomUUID } from "node:crypto";
import { AthenaBillingCapabilityError } from "../../errors.ts";
import type { BillingSqlExecutor } from "../../subject/repository.ts";

export type BillingPlanChangeState =
  | "requested"
  | "replacement_created"
  | "old_cancelled"
  | "completed"
  | "compensating"
  | "failed";

export interface BillingPlanChangeOperationRecord {
  expectedRowVersion: number;
  fencingEpoch: number;
  id: string;
  idempotencyKey: string;
  leaseToken: string | null;
  ownedSubscriptionId: string;
  priceId: string;
  replacementProviderSubscriptionId: string | null;
  state: BillingPlanChangeState;
  subjectId: string;
  subjectKind: "user" | "organization";
}

function asState(value: unknown): BillingPlanChangeState {
  switch (value) {
    case "requested":
    case "replacement_created":
    case "old_cancelled":
    case "completed":
    case "compensating":
    case "failed":
      return value;
    default:
      return "requested";
  }
}

function asRecord(
  row: Record<string, unknown>
): BillingPlanChangeOperationRecord {
  return {
    expectedRowVersion: Number(row.expected_row_version ?? 0),
    fencingEpoch: Number(row.fencing_epoch ?? 0),
    id: String(row.id),
    idempotencyKey: String(row.idempotency_key),
    leaseToken: typeof row.lease_token === "string" ? row.lease_token : null,
    ownedSubscriptionId: String(row.owned_subscription_id),
    priceId: String(row.price_id),
    replacementProviderSubscriptionId:
      typeof row.replacement_provider_subscription_id === "string"
        ? row.replacement_provider_subscription_id
        : null,
    state: asState(row.state),
    subjectId: String(row.subject_id),
    subjectKind: row.subject_kind === "organization" ? "organization" : "user",
  };
}

export async function beginPlanChangeOperation(input: {
  connectionId: string;
  enrollmentId?: string | null;
  expectedRowVersion: number;
  idempotencyKey: string;
  ownedSubscriptionId: string;
  priceId: string;
  sql: BillingSqlExecutor;
  subjectId: string;
  subjectKind: "user" | "organization";
}): Promise<BillingPlanChangeOperationRecord> {
  const existing = await input.sql.query(
    `SELECT *
		 FROM billing.billing_plan_change_operations
		 WHERE subject_kind = $1
		   AND subject_id = $2
		   AND idempotency_key = $3
		 LIMIT 1`,
    [input.subjectKind, input.subjectId, input.idempotencyKey]
  );
  if (existing.rows[0]) {
    return asRecord(existing.rows[0]);
  }
  const id = randomUUID();
  const leaseToken = randomUUID();
  const result = await input.sql.query(
    `INSERT INTO billing.billing_plan_change_operations (
			id,
			connection_id,
			subject_kind,
			subject_id,
			owned_subscription_id,
			enrollment_id,
			idempotency_key,
			expected_row_version,
			fencing_epoch,
			state,
			price_id,
			lease_token,
			lease_expires_at
		) VALUES (
			$1::uuid, $2::uuid, $3, $4, $5::uuid, $6::uuid, $7, $8, 1, 'requested', $9, $10,
			now() + interval '30 seconds'
		)
		RETURNING *`,
    [
      id,
      input.connectionId,
      input.subjectKind,
      input.subjectId,
      input.ownedSubscriptionId,
      input.enrollmentId ?? null,
      input.idempotencyKey,
      input.expectedRowVersion,
      input.priceId,
      leaseToken,
    ]
  );
  const row = result.rows[0];
  if (!row) {
    throw new AthenaBillingCapabilityError({
      message: "Another plan change is in progress.",
      operation: "self.subscription.change",
      reason: "unsupported_operation",
    });
  }
  return asRecord(row);
}

export async function transitionPlanChange(input: {
  expectedState: BillingPlanChangeState;
  id: string;
  leaseToken: string;
  replacementProviderSubscriptionId?: string | null;
  sql: BillingSqlExecutor;
  state: BillingPlanChangeState;
  lastError?: string | null;
}): Promise<BillingPlanChangeOperationRecord | undefined> {
  const result = await input.sql.query(
    `UPDATE billing.billing_plan_change_operations
		 SET state = $2,
		     replacement_provider_subscription_id = COALESCE($3, replacement_provider_subscription_id),
		     last_error = COALESCE($4, last_error),
		     fencing_epoch = fencing_epoch + 1,
		     lease_expires_at = now() + interval '30 seconds',
		     updated_at = now()
		 WHERE id = $1
		   AND state = $5
		   AND lease_token = $6
		 RETURNING *`,
    [
      input.id,
      input.state,
      input.replacementProviderSubscriptionId ?? null,
      input.lastError ?? null,
      input.expectedState,
      input.leaseToken,
    ]
  );
  const row = result.rows[0];
  return row ? asRecord(row) : undefined;
}

export async function completePlanChange(input: {
  id: string;
  leaseToken: string;
  sql: BillingSqlExecutor;
}): Promise<void> {
  await input.sql.query(
    `UPDATE billing.billing_plan_change_operations
		 SET state = 'completed',
		     lease_token = NULL,
		     lease_expires_at = NULL,
		     updated_at = now()
		 WHERE id = $1
		   AND lease_token = $2`,
    [input.id, input.leaseToken]
  );
}

export async function failPlanChange(input: {
  id: string;
  lastError: string;
  leaseToken: string;
  sql: BillingSqlExecutor;
}): Promise<void> {
  await input.sql.query(
    `UPDATE billing.billing_plan_change_operations
		 SET state = 'failed',
		     last_error = $2,
		     lease_token = NULL,
		     lease_expires_at = NULL,
		     updated_at = now()
		 WHERE id = $1
		   AND lease_token = $3`,
    [input.id, input.lastError, input.leaseToken]
  );
}
